import { TransformTool } from "@/apollo/types";
import { AuthFeatureGuard } from "@/components/elements/AuthFeatureGuard";
import { IconButton } from "@/components/elements/Button";
import { FeatureStateBadge } from "@/components/elements/FeatureStateBadge";
import {
  NavButton,
  NavDivider,
  Sidebar,
  useSidebar,
} from "@/components/elements/Sidebar";
import Tooltip from "@/components/elements/Tooltip";
import {
  AlertsIcon,
  DataSourceIcon,
  EditorIcon,
  GitHubIcon,
  HelpIcon,
  OrchestrationIcon,
  ReverseEtlIcon,
  SearchIcon,
  SettingsIcon,
} from "@/components/icons/domain-icons";
import { CurrentUserAvatar } from "@/components/modules/CurrentUserAvatar";
import { useActivityEnabled } from "@/features/activity";
import { useIntercom, useIsIntercomBlocked } from "@/features/intercom";
import { useCurrentTabLink } from "@/features/model-tool";
import { useSubscriptionStatus } from "@/features/subscription";
import { useTransformToolOptions } from "@/features/transformation-tool";
import cn, { default as classNames } from "@/helpers/classNames";
import { isWindows } from "@/helpers/isWindows";
import { IntegrationLogo } from "@/integrations";
import { useMixpanel } from "@/monitoring/mixpanel";
import { useDataWarehouseContext } from "@/providers/DataWarehouseProvider";
import { AccountType, useCurrentUser } from "@/providers/UserProvider";
import { useCurrentAccount } from "@/providers/account";
import { LinkWithSlug } from "@/router";
import dayjs from "@/utils/date";
import { Popover, Transition } from "@headlessui/react";
import { useKBar } from "kbar";
import { useFlags } from "launchdarkly-react-client-sdk";
import {
  ActivityIcon,
  BookTextIcon,
  ChevronLeftIcon,
  CircleHelpIcon,
  DownloadIcon,
  HouseIcon,
  MessageCircleMoreIcon,
  RocketIcon,
  TriangleAlert,
} from "lucide-react";
import React, { forwardRef } from "react";
import { createPortal } from "react-dom";
import { usePopper } from "react-popper";
import { WorkspaceSwitcher } from "../WorkspaceSwitcher";
import SidebarDownloads, {
  useSidebarDownloads,
} from "../downloads/SidebarDownloads";
import SidebarNotifications, {
  useSidebarNotifications,
} from "../notifications/SidebarNotifications";
import OnboardingChecklistButton from "./OnboardingChecklistButton";
import { TrialBox } from "./TrialBox";

export function WorkspaceSidebar({
  children: _children,
  ...sidebarProps
}: Omit<React.ComponentProps<typeof Sidebar>, "children"> & {
  children?: React.ReactNode;
}) {
  return (
    <Sidebar {...sidebarProps}>
      <div className="flex h-full flex-col border-r border-gray-200 bg-sidebar text-sidebar-foreground dark:border-gray-700">
        <div className="relative h-full overflow-y-auto p-2">
          <WorkspaceSidebarContent />
        </div>
        <div className="flex flex-col px-2 pb-2">
          <NavDivider className="mt-0" />
          <SidebarCollapseButton />
        </div>
      </div>
    </Sidebar>
  );
}

function showTrialBox(
  trial: ReturnType<typeof useSubscriptionStatus>["trial"],
): trial is NonNullable<typeof trial> {
  if (!trial || trial.isExpired) return false;
  return dayjs(trial.trialEndDate).diff(dayjs(), "day") <= 5;
}

export function WorkspaceSidebarContent() {
  const modelToolTabLink = useCurrentTabLink();

  const dwh = useDataWarehouseContext();
  const { trial } = useSubscriptionStatus();
  const showTrial = showTrialBox(trial);
  const account = useCurrentAccount();

  const { isCollapsed } = useSidebar();
  const transformToolOptions = useTransformToolOptions();
  const activityEnabled = useActivityEnabled();
  return (
    <div className="flex h-full w-full flex-col gap-1">
      <div className="flex flex-col gap-1">
        {activityEnabled && (
          <>
            <NavLink to={"/overview"} icon={<HouseIcon className="size-5" />}>
              Overview
            </NavLink>
            <NavDivider />
          </>
        )}
        {dwh.features.isExtractEnabled && (
          <NavLink to={"/sources"} icon={<DataSourceIcon />}>
            Data Sources
          </NavLink>
        )}
        {dwh.features.isActivateEnabled && (
          <NavLink to={"/reverse-etl"} icon={<ReverseEtlIcon />}>
            Reverse ETL
          </NavLink>
        )}
        <NavDivider />

        {dwh.features.isTransformEnabled && (
          <>
            <NavLink
              to={modelToolTabLink}
              icon={<EditorIcon />}
              className="relative"
              tooltip="Transform"
            >
              Transform
              {!activityEnabled &&
                account.accountSettings.transformTool === null && (
                  <div className="absolute top-1/2 -left-0.5 flex size-1.5 -translate-y-1/2 animate-pulse rounded-full bg-sky-500" />
                )}
            </NavLink>
          </>
        )}
        <NavLink
          to={"/orchestration"}
          icon={
            <div className="flex size-5 items-center justify-center">
              <OrchestrationIcon className="size-[18px]" />
            </div>
          }
        >
          Orchestration
        </NavLink>
        <NavDivider />
        <AuthFeatureGuard permissions={["manage:all", "read:User"]}>
          <>
            {dwh.features.isTransformEnabled &&
              account.accountSettings.transformTool === TransformTool.Github &&
              (() => {
                const githubOption =
                  transformToolOptions.optionsMap[TransformTool.Github];
                const featureFlag = githubOption.featureFlag;
                return (
                  <NavButton
                    as={LinkWithSlug}
                    to={"/settings/github"}
                    icon={
                      <div className="relative">
                        <GitHubIcon className="size-5" />
                        {isCollapsed && featureFlag && (
                          <div className="absolute -top-0.5 -right-1 size-1.5 rounded-full bg-violet-700" />
                        )}
                      </div>
                    }
                    tooltip={
                      <div className="flex items-center gap-2">
                        GitHub integration settings{" "}
                        {featureFlag && (
                          <FeatureStateBadge
                            featureState={featureFlag}
                            className="ml-auto border-none bg-violet-400/10 px-1.5 py-px text-1xs text-violet-300 dark:border dark:border-violet-200 dark:text-violet-700"
                          />
                        )}
                      </div>
                    }
                  >
                    <div className="flex">
                      GitHub{" "}
                      {featureFlag && (
                        <FeatureStateBadge
                          featureState={featureFlag}
                          className="ml-auto px-1.5 py-px text-1xs"
                        />
                      )}
                    </div>
                  </NavButton>
                );
              })()}
          </>
        </AuthFeatureGuard>
        <AuthFeatureGuard permissions={["manage:all", "read:User"]}>
          <NavLink to={"/settings"} icon={<SettingsIcon />}>
            Settings
          </NavLink>
        </AuthFeatureGuard>
        {showTrial && <TrialBox {...trial} />}
      </div>
      <div className="mt-auto mb-1 flex flex-col gap-1">
        <OnboardingChecklistButton />
        <SidebarDownloads>
          <DownloadsNavButton />
        </SidebarDownloads>
        <SidebarNotifications>
          <NotificationNavButton />
        </SidebarNotifications>
        <SidebarHelpDialog>
          <NavButton icon={<HelpIcon />}>Help</NavButton>
        </SidebarHelpDialog>
        <KbarSearchButton />
        <NavDivider unstyled />
        <UserProfileLink />
        <WorkspaceSwitcher allowCreateDestination>
          {(currentAccount) => (
            <WorkspaceSwitcherButton account={currentAccount} />
          )}
        </WorkspaceSwitcher>
      </div>
    </div>
  );
}

function KbarSearchButton() {
  const kbar = useKBar();
  const mixpanel = useMixpanel();
  return (
    <NavButton
      onClick={() => {
        kbar.query.toggle();
        mixpanel.track("Kbar Opened", {
          source: "sidebar",
        });
      }}
      icon={<SearchIcon />}
      tooltip={
        <>
          Search{" "}
          <span className="ml-2 text-background/75">
            {isWindows() ? "Ctrl + k" : "⌘ + k"}
          </span>
        </>
      }
    >
      Search
    </NavButton>
  );
}

function SidebarCollapseButton() {
  const { isCollapsed, toggleCollapseState } = useSidebar();
  return (
    <IconButton
      icon={
        <ChevronLeftIcon
          className={classNames(
            "size-5 transition-all",
            isCollapsed && "rotate-180",
          )}
        />
      }
      onClick={() => toggleCollapseState()}
      variant="ghost"
      size="sm"
      className={classNames("self-end rounded-sm", isCollapsed && "mr-0.5")}
    />
  );
}

function DownloadsNavButton() {
  const { downloadsCount, toggleSidebar } = useSidebarDownloads();
  const hasDownloads = downloadsCount > 0;
  const downloadsCountFormatted = downloadsCount > 9 ? "9+" : downloadsCount;

  // Most of the time users won't have any downloads, so keep the entry point
  // out of the sidebar until there's actually something to show/manage.
  if (!hasDownloads) return null;

  return (
    <NavButton
      icon={
        <div className="relative">
          <DownloadIcon className="size-5" />
          {hasDownloads && (
            <div className="absolute top-[-8px] right-[-7px] flex h-4 min-w-4 items-center justify-center rounded-sm bg-blue-500 px-0.5 text-xs font-medium text-white">
              {downloadsCountFormatted}
            </div>
          )}
        </div>
      }
      onClick={() => toggleSidebar()}
    >
      Downloads
    </NavButton>
  );
}

function NotificationNavButton() {
  const { notificationsCount, toggleSidebar } = useSidebarNotifications();
  const hasNotifications = notificationsCount > 0;
  const notificationsCountFormatted =
    notificationsCount > 9 ? "9+" : notificationsCount;
  return (
    <NavButton
      icon={
        <div className="relative">
          <AlertsIcon className="size-5" />
          {hasNotifications && (
            <div className="absolute top-[-8px] right-[-7px] flex h-4 min-w-4 items-center justify-center rounded-sm bg-red-500 px-0.5 text-xs font-medium text-white">
              {notificationsCountFormatted}
            </div>
          )}
        </div>
      }
      onClick={() => toggleSidebar()}
    >
      {hasNotifications ? "View alerts" : "No sync alerts"}
    </NavButton>
  );
}

function NavLink(
  props: { to: string } & React.ComponentProps<
    typeof NavButton<typeof LinkWithSlug>
  >,
) {
  return (
    <NavButton
      as={LinkWithSlug}
      {...props}
      getActiveProps={() => ({
        className: "text-blue-500 bg-black/10 dark:bg-white/10",
      })}
    />
  );
}

const SidebarHelpDialog = (props: { children: React.ReactNode }) => {
  const { intercomChat } = useFlags();

  const popperElRef = React.useRef(null);
  let [referenceElement, setReferenceElement] =
    React.useState<HTMLButtonElement | null>();
  let [popperElement, setPopperElement] =
    React.useState<HTMLDivElement | null>();
  let { styles, attributes } = usePopper(referenceElement, popperElement, {
    strategy: "absolute",
    placement: "auto",
    modifiers: [
      { name: "preventOverflow", enabled: true },
      { name: "offset", options: { offset: [0, 12] } },
    ],
  });

  const Trigger = React.Children.only(props.children);
  return (
    <Popover>
      <Popover.Button as="div">
        {React.isValidElement<any>(Trigger)
          ? React.cloneElement(Trigger, {
              ref: setReferenceElement,
              role: "button",
            })
          : null}
      </Popover.Button>
      {createPortal(
        <div
          ref={popperElRef}
          style={styles.popper}
          {...attributes.popper}
          className="relative z-50 shadow-lg"
        >
          <Transition
            enter="transition duration-100 ease-out"
            enterFrom="scale-95 opacity-0"
            enterTo="scale-100 opacity-100"
            leave="transition duration-75 ease-out"
            leaveFrom="scale-100 opacity-100"
            leaveTo="scale-95 opacity-0"
            beforeEnter={() => setPopperElement(popperElRef.current)}
            afterLeave={() => setPopperElement(null)}
          >
            <Popover.Panel className="flex min-w-52 flex-col space-y-2 rounded-sm border bg-popover p-2 text-left text-sm text-popover-foreground">
              {({ close }) => (
                <>
                  <NavButton
                    as="a"
                    href="https://weld.app/docs"
                    target="_blank"
                    rel="noreferrer"
                    icon={<BookTextIcon className="size-4" />}
                  >
                    Documentation
                  </NavButton>
                  <NavButton
                    as="a"
                    href="https://weld.app/docs/quickstart-guide"
                    target="_blank"
                    rel="noreferrer"
                    icon={<RocketIcon className="size-4" />}
                  >
                    Quick Start Guide
                  </NavButton>
                  <NavButton
                    as="a"
                    href="https://weld.app/docs/faq"
                    target="_blank"
                    rel="noreferrer"
                    icon={<CircleHelpIcon className="size-4" />}
                  >
                    FAQ
                  </NavButton>
                  {intercomChat && <IntercomChatHelpItem onSelect={close} />}
                  <NavButton
                    as="a"
                    href="https://status.weld.app/"
                    target="_blank"
                    rel="noreferrer"
                    icon={<ActivityIcon className="size-4" />}
                  >
                    Status Page
                  </NavButton>
                </>
              )}
            </Popover.Panel>
          </Transition>
        </div>,
        document.body,
      )}
    </Popover>
  );
};

const IntercomChatHelpItem = ({ onSelect }: { onSelect: () => void }) => {
  const intercom = useIntercom();
  const isBlocked = useIsIntercomBlocked();

  const button = (
    <NavButton
      onClick={() => {
        intercom.show();
        // The messenger takes over the corner this menu sits in, so leaving it
        // open would stack one panel on the other.
        onSelect();
      }}
      icon={
        isBlocked ? (
          <TriangleAlert className="size-4 text-amber-500" />
        ) : (
          <MessageCircleMoreIcon className="size-4" />
        )
      }
    >
      Chat with us
    </NavButton>
  );

  if (!isBlocked) return button;

  return (
    <Tooltip
      placement="right"
      content="Chat may be blocked by an ad blocker or privacy extension. Click for details."
    >
      {button}
    </Tooltip>
  );
};

const WorkspaceSwitcherButton = forwardRef(
  (
    {
      account,
    }: {
      account: AccountType | undefined;
    },
    ref: React.ForwardedRef<HTMLButtonElement>,
  ) => {
    const ctx = useSidebar();
    return (
      <NavButton
        ref={ref}
        icon={
          account?.dataWarehouse ? (
            <div className="flex size-6 items-center justify-center">
              <IntegrationLogo id={account.dataWarehouse.integrationId} />
            </div>
          ) : (
            <div />
          )
        }
        className={cn("px-1.5 py-1 font-normal", {
          "my-0.5 py-0.5": ctx.isCollapsed,
        })}
      >
        {account ? (
          <div className="flex w-full flex-col">
            <div className="truncate text-sm leading-tight font-medium">
              {account.name}
            </div>
            <span className="truncate text-xs text-gray-400 dark:text-gray-400">
              {account.dataWarehouse ? (
                <>
                  {account.dataWarehouse?.title}{" "}
                  {account.dataWarehouse?.subTitle &&
                    `(${account.dataWarehouse?.subTitle})`}
                </>
              ) : (
                <em>Data warehouse not connected</em>
              )}
            </span>
          </div>
        ) : (
          <em>No account selected</em>
        )}
      </NavButton>
    );
  },
);

function UserProfileLink() {
  const user = useCurrentUser();
  const ctx = useSidebar();
  return (
    <NavLink
      icon={<CurrentUserAvatar size="lg" className="size-6" />}
      className={cn("px-1.5 py-1 font-normal", {
        "my-0.5 py-0.5": ctx.isCollapsed,
      })}
      to="/settings/user-profile"
    >
      <div className="flex w-full flex-col">
        <span className="max-w-full truncate text-sm leading-tight font-medium">
          {user.firstName ? (
            <span>
              {user.firstName} {user.lastName || ""}
            </span>
          ) : (
            <span className="text-danger">Click to add your name</span>
          )}
        </span>
        <span className="truncate text-xs text-gray-400 dark:text-gray-400">
          {user.email}
        </span>
      </div>
    </NavLink>
  );
}
